import React, { useEffect, useRef, useState } from "react";
import UIkit from "uikit";
import SchedulerService from "../../../services/scheduler.service";
import ReservationsService from "../../../services/reservations.service";
import ReservationScheduledMessages, { scheduledMessagesForBooking } from "../../guests/reservation-scheduled-messages";
import { usePermissionsStore } from "../../permissions/permissions_store";

export default function ScheduledMessagesPanel({ reservation }) {
  const { canView, canEdit } = usePermissionsStore();
  const canRead = canView("messaging.scheduler");
  const canSend = canEdit("messaging.guests_message");
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [sentByBooking, setSentByBooking] = useState({});
  const [sending, setSending] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const sendingRef = useRef(false);
  const mounted = useRef(false);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  // Load on entering Messages, including when Guests has never been opened.
  useEffect(() => {
    if (!canRead) {
      setMessages([]);
      setLoading(false);
      return;
    }
    let active = true;
    setLoading(true);
    setError(false);
    SchedulerService.selectScheduledMessageList().then(({ data }) => {
      if (active) setMessages(Array.isArray(data) ? data : []);
    }).catch(() => { if (active) setError(true); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [attempt, canRead]);

  const send = async (reservationId, messageId) => {
    if (!canSend) return;
    if (sendingRef.current) return;
    sendingRef.current = true;
    setSending({ reservation_id: reservationId, msg_id: messageId });
    try {
      const { data } = await ReservationsService.sendScheduledMessage(reservationId, messageId);
      if (!data?.status || data.error) throw new Error("Send failed");
      if (!mounted.current) return;
      setSentByBooking((previous) => ({ ...previous,
        [reservationId]: [...(previous[reservationId] || []), data.status],
      }));
      UIkit.notification("Scheduled message sent successfully", "success");
    } catch {
      if (mounted.current) UIkit.notification("Could not send the scheduled message. Please try again.", "danger");
    } finally {
      sendingRef.current = false;
      if (mounted.current) setSending(null);
    }
  };

  if (!canRead || !reservation?.guest_reservation_id || reservation.category === "CXL") return null;
  const booking = { ...reservation, sent_scheduled_messages: [
    ...(reservation.sent_scheduled_messages || []), ...(sentByBooking[reservation.guest_reservation_id] || []),
  ] };
  const visibleMessages = scheduledMessagesForBooking(messages, booking);
  return <section className="hms-guest-identity-card message-scheduled-messages" aria-label="Scheduled messages" aria-busy={loading}>
    <button type="button" aria-expanded={expanded} onClick={() => setExpanded(!expanded)}>
      <span>Scheduled Messages{!loading && !error ? ` (${visibleMessages.length})` : ""}</span>
      <span aria-hidden="true">{expanded ? "−" : "+"}</span>
    </button>
    {expanded && <div className="message-scheduled-messages-content">
      {loading ? <div role="status">Loading scheduled messages...</div>
        : error ? <div role="alert">Could not load scheduled messages. <button type="button" onClick={() => setAttempt((value) => value + 1)}>Retry</button></div>
          : visibleMessages.length ? <ReservationScheduledMessages
            reservation={booking} messages={messages} sendingMsg={sending} onSend={send} readOnly={!canSend} />
            : <div className="secondary">No scheduled messages for this booking.</div>}
    </div>}
  </section>;
}
